import * as React from 'react';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import Tooltip from '@mui/material/Tooltip';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Collapse from '@mui/material/Collapse';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import HelpRoundedIcon from '@mui/icons-material/HelpRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded';
import AccountBalanceRoundedIcon from '@mui/icons-material/AccountBalanceRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import CategoryRoundedIcon from '@mui/icons-material/CategoryRounded';
import PieChartRoundedIcon from '@mui/icons-material/PieChartRounded';
import RestoreRoundedIcon from '@mui/icons-material/RestoreRounded';
import PhoneAndroidRoundedIcon from '@mui/icons-material/PhoneAndroidRounded';
import SwapHorizRoundedIcon from '@mui/icons-material/SwapHorizRounded';
import ShoppingCartRoundedIcon from '@mui/icons-material/ShoppingCartRounded';
import StorageRoundedIcon from '@mui/icons-material/StorageRounded';
import ListAltRoundedIcon from '@mui/icons-material/ListAltRounded';
import InventoryRoundedIcon from '@mui/icons-material/InventoryRounded';
import BuildRoundedIcon from '@mui/icons-material/BuildRounded';
import SellRoundedIcon from '@mui/icons-material/SellRounded';
import LabelRoundedIcon from '@mui/icons-material/LabelRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import { Link, useLocation } from 'react-router-dom';
import { LocalShipping, PointOfSale } from '@mui/icons-material';
import { useTawk } from '../hooks/useTawk';
import usePermissions from '../hooks/usePermissions';

const C = {
  text: 'rgba(255,255,255,0.88)',
  textActive: '#ffffff',
  icon: '#ffffff',
  iconActive: '#ffffff',
  activeBg: 'rgba(255,255,255,0.12)',
  hoverBg: 'rgba(255,255,255,0.07)',
  sectionLabel: 'rgba(255,255,255,0.55)',
  sectionIcon: '#ffffff',
  bottomText: 'rgba(255,255,255,0.82)',
  bottomIcon: '#ffffff',
};

const SUB_ICONS = {
  Ventas: StorefrontRoundedIcon,
  Prestamos: AccountBalanceRoundedIcon,
  'Centro de Pagos': PaymentsRoundedIcon,
  Inversionistas: TrendingUpRoundedIcon,
  'Productos Financieros': CategoryRoundedIcon,
  Portafolio: PieChartRoundedIcon,
  Recuperacion: RestoreRoundedIcon,
  'Cobros Móviles': PhoneAndroidRoundedIcon,
  Traspaso: SwapHorizRoundedIcon,
  Compras: ShoppingCartRoundedIcon,
  Almacenes: StorageRoundedIcon,
  'Lista de Precios': ListAltRoundedIcon,
  'Productos y servicios': InventoryRoundedIcon,
  'Categorias de productos': SellRoundedIcon,
  'Centro de Servicios': BuildRoundedIcon,
};

function NavItem({ text, to, collapsed }) {
  const location = useLocation();
  const isActive = location.pathname === to;
  const IconComponent = SUB_ICONS[text] || LabelRoundedIcon;

  if (collapsed) {
    return (
      <Tooltip title={text} placement="right" arrow>
        <ListItem disablePadding sx={{ display: 'block', mb: 0.25 }}>
          <ListItemButton
            component={Link}
            to={to}
            sx={{
              justifyContent: 'center',
              py: 0.8,
              mx: 0.5,
              borderRadius: 1.5,
              transition: 'background 0.15s',
              bgcolor: isActive ? C.activeBg : 'transparent',
              '&:hover': { bgcolor: isActive ? C.activeBg : C.hoverBg },
            }}
          >
            <IconComponent sx={{ fontSize: 18, color: isActive ? C.iconActive : C.icon }} />
          </ListItemButton>
        </ListItem>
      </Tooltip>
    );
  }

  return (
    <ListItem disablePadding sx={{ display: 'block', mb: 0.25 }}>
      <ListItemButton
        component={Link}
        to={to}
        sx={{
          mx: 1,
          px: 1.5,
          py: 0.7,
          borderRadius: 1.5,
          gap: 1.25,
          minHeight: 36,
          transition: 'background 0.15s',
          bgcolor: isActive ? C.activeBg : 'transparent',
          '&:hover': { bgcolor: isActive ? C.activeBg : C.hoverBg },
        }}
      >
        <IconComponent
          sx={{ fontSize: 16, color: isActive ? C.iconActive : C.icon, flexShrink: 0 }}
        />
        <Typography
          variant="body2"
          style={{
            color: isActive ? C.textActive : C.text,
            fontWeight: isActive ? 600 : 400,
            fontSize: '0.81rem',
            flex: 1,
            lineHeight: 1,
          }}
        >
          {text}
        </Typography>
        {text === 'Centro de Pagos' && (
          <OpenInNewRoundedIcon sx={{ fontSize: 12, color: C.icon }} />
        )}
        {isActive && (
          <Box
            sx={{
              width: 5,
              height: 5,
              borderRadius: '50%',
              bgcolor: 'hsl(210,100%,65%)',
              flexShrink: 0,
            }}
          />
        )}
      </ListItemButton>
    </ListItem>
  );
}

function SectionHeader({ label, icon: Icon, open, onClick, collapsed }) {
  if (collapsed) return null;

  return (
    <ListItemButton
      onClick={onClick}
      sx={{
        mx: 1,
        px: 1.5,
        py: 0.6,
        borderRadius: 1.5,
        gap: 1.25,
        mb: 0.5,
        '&:hover': { bgcolor: C.hoverBg },
      }}
    >
      <Icon sx={{ fontSize: 17, color: '#ffffff' }} style={{color:"white"}} />
      <Typography
        style={{fontWeight:"bold"}}
        sx={{ color: 'white', fontWeight: 700, fontSize: '0.90rem', flex: 1 }}
      >
        {label}
      </Typography>
      <ExpandMoreRoundedIcon
        style={{color:"white"}}
        sx={{
          fontSize: 16,
          color: 'white',
          transition: 'transform 0.2s',
          transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
        }}
      />
    </ListItemButton>
  );
}

export default function MenuContent({ collapsed = false }) {
  const [openLogistica, setOpenLogistica] = React.useState(false);
  const [openGestionComercial, setOpenGestionComercial] = React.useState(false);
  const location = useLocation();
  const { hasPermission, hasModule } = usePermissions();

  const [modulosGestionComercial, setModulosGestionComercial] = React.useState([
    { text: 'Ventas', to: '/gestion_comercial/ventas', visible: true, modulo: 'productos_catalogo' },
    { text: 'Prestamos', to: '/gestion_comercial/prestamos', visible: true, modulo: 'prestamos', permisoRequerido: ['prestamos', 'ver'] },
    { text: 'Promesas de Pago', to: '/gestion_comercial/prestamos/promesas_pago', visible: true, modulo: 'prestamos', permisoRequerido: ['prestamos', 'ver'] },
    { text: 'Centro de Pagos', to: '/gestion_comercial/centro_cobros', visible: true, modulo: 'prestamos', permisoRequerido: ['pagos', 'ver'] },
    { text: 'Inversionistas', to: '/gestion_comercial/inversionistas', visible: true, modulo: 'inversionistas' },
    { text: 'Productos Financieros', to: '/gestion_comercial/productos_financieros', visible: true, modulo: 'productos_financieros', permisoRequerido: ['productos_financieros', 'ver'] },
    { text: 'Portafolio', to: '/gestion_comercial/portafolio', visible: true, modulo: 'portafolio', permisoRequerido: ['portafolio', 'ver'] },
    { text: 'Recuperacion', to: '/gestion_comercial/recuperacion', visible: true, modulo: 'recuperacion', permisoRequerido: ['recuperaciones', 'ver'] },
    { text: 'Cobros Móviles', to: '/gestion_comercial/cobros_moviles', visible: true, modulo: 'prestamos' },
    { text: 'Categorias de Cargos', to: '/gestion_comercial/categorias_cargos', visible: true, modulo: 'cargos', permisoRequerido: ['categorias_cargos', 'ver'] },
    { text: 'Traspaso', to: '/gestion_comercial/traspaso', visible: true, modulo: 'traspaso', permisoRequerido: ['traspaso', 'ver'] },
  ]);
  const [modulosLogistica, setModulosLogistica] = React.useState([
    { text: 'Compras', to: '/logistica/compras', visible: true, modulo: 'compras' },
    { text: 'Almacenes', to: '/logistica/almacenes', visible: true, modulo: 'productos_catalogo', permisoRequerido: ['almacenes', 'ver'] },
    { text: 'Transferencias de Mercancía', to: '/logistica/transferencias', visible: true, modulo: 'transferencias' },
    { text: 'Lista de Precios', to: '/logistica/Listasdeprecios', visible: true, modulo: 'compras', permisoRequerido: ['lista_precios', 'ver'] },
    { text: 'Productos y servicios', to: '/logistica/productos', visible: true, modulo: 'productos_catalogo', permisoRequerido: ['productos', 'ver'] },
    { text: 'Categorias de productos', to: '/logistica/categorias', visible: true, modulo: 'productos_catalogo', permisoRequerido: ['categorias_inventario', 'ver'] },
    { text: 'Centro de Servicios', to: '/logistica/Centrodeservicios', visible: true, modulo: 'taller' },
  ]);

  // ——————————————————————————————
  // Tawk integration
  // ——————————————————————————————
  const isProd = 'production';
  const propertyId = process.env.REACT_APP_TAWK_PROPERTY_ID;
  const widgetId = process.env.REACT_APP_TAWK_WIDGET_ID || 'default';

  const visitor = (() => {
    try {
      const name = localStorage.getItem('nombre');
      const usuario = localStorage.getItem('usuario');
      const company = localStorage.getItem('nombre_compania');
      const idcompany = localStorage.getItem('idcompany');
      const branch = localStorage.getItem('nombre_sucursal');
      const idbranch = localStorage.getItem('id_sucursal');
      const tawkHash = localStorage.getItem('tawkHash');

      const visitorData = {};
      if (name) visitorData.name = name;
      if (usuario) visitorData.usuario = usuario;
      if (branch) visitorData.branch = branch;
      if (company) visitorData.company = company;
      if (idcompany) visitorData.idcompany = idcompany;
      if (idbranch) visitorData.idbranch = idbranch;
      if (tawkHash) visitorData.tawkHash = tawkHash;

      return Object.keys(visitorData).length > 0 ? visitorData : null;
    } catch (error) {
      console.error('Error creating visitor data:', error);
      return null;
    }
  })();

 // const { api } = useTawk({
   // propertyId,
   // widgetId,
   // enabled: isProd,
    //visitor,
    //onReady: (tawk) => {
    //  tawk.addEvent('Menu mounted', { path: location.pathname });
   // },
  //});

//  React.useEffect(() => {
//    if (api && visitor) {
  //    api.setAttributes(visitor, (err) => {
    //    if (err) console.log('Error enviando atributos a Tawk:', err);
      //});
//    }
  //}, [api, visitor]);

  //const openSupport = React.useCallback(() => {
//    if (api) {
     // api.showWidget();
//      api.maximize();
    //  api.addTags(['Bankivo', 'Menu Support']);
  //    api.addEvent('Open support from menu', { path: location.pathname });
//    } else if (propertyId) {
    //  const url = `https://tawk.to/chat/${propertyId}/${widgetId || 'default'}`;
//      window.open(url, '_blank', 'noopener,noreferrer');
  //  }
//  }, [api, propertyId, widgetId, location.pathname]);

  // ——————————————————————————————

  React.useEffect(() => {
    const fetchCategoriasCargos = async () => {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/categorias_cargos/company/${localStorage.getItem('idcompany')}?gestion_operativa=1`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-Usuario-ID': localStorage.getItem('id'),
            'X-company-ID': localStorage.getItem('idcompany'),
          },
        }
      );
      const data = await response.json();

      const categoriasItems =
        Array.isArray(data) && data.length > 0
          ? data.map((categoria) => ({
              text: categoria.nombre,
              to: `/gestion_comercial/${categoria.id}`,
              visible: true,
              modulo: 'categorias_cargos',
              permisoRequerido: ['categorias_cargos', 'ver'],
            }))
          : [];

      setModulosGestionComercial((prev) => {
        const sinDinamicos = prev.filter((m) => m.modulo !== 'categorias_cargos' || !m.dinamico);
        return sinDinamicos.concat(categoriasItems.map((c) => ({ ...c, dinamico: true })));
      });
    };
    fetchCategoriasCargos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const itemEsVisible = React.useCallback(
    (m) =>
      m.visible &&
      hasModule(m.modulo) &&
      (!m.permisoRequerido || hasPermission(m.permisoRequerido[0], m.permisoRequerido[1])),
    [hasModule, hasPermission]
  );

  const modulosGestionComercialVisibles = React.useMemo(
    () => modulosGestionComercial.filter(itemEsVisible),
    [modulosGestionComercial, itemEsVisible]
  );

  const modulosLogisticaVisibles = React.useMemo(
    () => modulosLogistica.filter(itemEsVisible),
    [modulosLogistica, itemEsVisible]
  );

  const isDashboard = location.pathname === '/dashboard';

  const isSettings = location.pathname === '/settings';

  return (
    <Stack sx={{ flexGrow: 1, py: 0.5, justifyContent: 'space-between' }}>
      <List dense disablePadding>
        {/* Home */}
        <Tooltip title={collapsed ? 'Inicio' : ''} placement="right" arrow>
          <ListItem disablePadding sx={{ display: 'block', mb: 0.5 }}>
            <ListItemButton
              component={Link}
              to="/dashboard"
              sx={{
                mx: collapsed ? 0.5 : 1,
                px: collapsed ? 0 : 1.5,
                py: 0.75,
                justifyContent: collapsed ? 'center' : 'flex-start',
                borderRadius: 1.5,
                gap: 1.25,
                transition: 'background 0.15s',
                bgcolor: isDashboard ? C.activeBg : 'transparent',
                '&:hover': { bgcolor: isDashboard ? C.activeBg : C.hoverBg },
              }}
            >
              <HomeRoundedIcon sx={{ fontSize: 18, color: isDashboard ? C.iconActive : C.icon }} style={{color:"white"}}/>
              {!collapsed && (
                <Typography
                  style={{fontWeight:"bold"}}
                  sx={{ color: isDashboard ? C.textActive : C.text, fontWeight: isDashboard ? 600 : 400, fontSize: '0.90rem' }}
                >
                  Inicio
                </Typography>
              )}
            </ListItemButton>
          </ListItem>
        </Tooltip>

        {/* Gestión Comercial */}
        {modulosGestionComercialVisibles.length > 0 && (
          <ListItem disablePadding sx={{ display: 'block', mt: collapsed ? 0.5 : 1 }}>
            <SectionHeader
              label="Gestión Comercial"
              icon={PointOfSale}
              open={modulosLogisticaVisibles.length === 0 ? true : openGestionComercial}
              onClick={() => setOpenGestionComercial((v) => !v)}
              collapsed={collapsed}
            />
          </ListItem>
        )}
        {modulosGestionComercialVisibles.length > 0 && (
          <Collapse in={collapsed || (modulosLogisticaVisibles.length === 0 ? true : openGestionComercial)} timeout="auto" unmountOnExit>
            {collapsed ? (
              modulosGestionComercialVisibles.map(({ text, to }) => (
                <NavItem key={to} text={text} to={to} collapsed={collapsed} />
              ))
            ) : (
              <Box sx={{ ml: 2.5, borderLeft: '1px solid rgba(255,255,255,0.12)', mb: 0.5 }}>
                {modulosGestionComercialVisibles.map(({ text, to }) => (
                  <NavItem key={to} text={text} to={to} collapsed={collapsed} />
                ))}
              </Box>
            )}
          </Collapse>
        )}

        {/* Logística */}
        {modulosLogisticaVisibles.length > 0 && (
          <ListItem disablePadding sx={{ display: 'block', mt: collapsed ? 0.5 : 1 }}>
            <SectionHeader
              label="Logística y Almacén"
              icon={LocalShipping}
              open={modulosGestionComercialVisibles.length === 0 ? true : openLogistica}
              onClick={() => setOpenLogistica((v) => !v)}
              collapsed={collapsed}
            />
          </ListItem>
        )}
        {modulosLogisticaVisibles.length > 0 && (
          <Collapse in={collapsed || (modulosGestionComercialVisibles.length === 0 ? true : openLogistica)} timeout="auto" unmountOnExit>
            {collapsed ? (
              modulosLogisticaVisibles.map(({ text, to }) => (
                <NavItem key={to} text={text} to={to} collapsed={collapsed} />
              ))
            ) : (
              <Box sx={{ ml: 2.5, borderLeft: '1px solid rgba(255,255,255,0.12)', mb: 0.5 }}>
                {modulosLogisticaVisibles.map(({ text, to }) => (
                  <NavItem key={to} text={text} to={to} collapsed={collapsed} />
                ))}
              </Box>
            )}
          </Collapse>
        )}
      </List>

      {/* Bottom items */}
      <List dense disablePadding sx={{ pb: 1 }}>
        <Box sx={{ mx: 2, mb: 1, borderTop: '1px solid rgba(255,255,255,0.07)' }} />

        <Tooltip title={collapsed ? 'Soporte' : ''} placement="right" arrow>
          <ListItem disablePadding sx={{ display: 'block', mb: 0.25 }}>
            <ListItemButton
//              onClick={openSupport}
              sx={{
                mx: collapsed ? 0.5 : 1,
                px: collapsed ? 0 : 1.5,
                py: 0.7,
                justifyContent: collapsed ? 'center' : 'flex-start',
                borderRadius: 1.5,
                gap: 1.25,
                '&:hover': { bgcolor: C.hoverBg },
              }}
            >
              <HelpRoundedIcon sx={{ fontSize: 17, color: C.bottomIcon }} />
              {!collapsed && (
                <ListItemText primary="Soporte" slotProps={{ primary: { sx: { color: C.bottomText, fontSize: '0.81rem' } } }} />
              )}
            </ListItemButton>
          </ListItem>
        </Tooltip>

        <Tooltip title={collapsed ? 'Configuración' : ''} placement="right" arrow>
          <ListItem disablePadding sx={{ display: 'block' }}>
            <ListItemButton
              component={Link}
              to="/settings"
              sx={{
                mx: collapsed ? 0.5 : 1,
                px: collapsed ? 0 : 1.5,
                py: 0.7,
                justifyContent: collapsed ? 'center' : 'flex-start',
                borderRadius: 1.5,
                gap: 1.25,
                bgcolor: isSettings ? C.activeBg : 'transparent',
                '&:hover': { bgcolor: isSettings ? C.activeBg : C.hoverBg },
              }}
            >
              <SettingsRoundedIcon sx={{ fontSize: 17, color: isSettings ? C.iconActive : C.bottomIcon }} />
              {!collapsed && (
                <ListItemText
                  primary="Configuración"
                  slotProps={{ primary: { sx: { color: isSettings ? C.textActive : C.bottomText, fontSize: '0.81rem', fontWeight: isSettings ? 600 : 400 } } }}
                />
              )}
            </ListItemButton>
          </ListItem>
        </Tooltip>
      </List>
    </Stack>
  );
}
